import { type DependencyList, useCallback, useEffect, useState } from 'react';

/** Load data when the component mounts or `deps` change. `reload()` fetches again. */
export function useLoad<T>(load: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the caller lists what `load` depends on
  useEffect(() => {
    let alive = true;
    setLoading(true);
    load()
      .then((value) => {
        if (!alive) return;
        setData(value);
        setError(null);
      })
      .catch((err) => alive && setError(err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}
